import { useState } from "react";
import { NavLink } from "react-router-dom";
import { CalendarDays, ClipboardList, Users, ListChecks, Plane, Inbox, Bell, ShieldCheck, LogOut, UserCog, Building2, Settings, KeyRound, FolderKanban, Mail } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { can, initials } from "@/lib/colors";
import { NotificationBell } from "@/components/layout/NotificationBell";
import { ChangePasswordDialog } from "@/components/layout/ChangePasswordDialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const MAIN_NAV = [
  { to: "/tasks", label: "My tasks", short: "Tasks", icon: ListChecks },
  { to: "/", label: "Calendar", short: "Calendar", icon: CalendarDays },
  { to: "/roster", label: "My schedule", short: "Schedule", icon: ClipboardList, perms: ["own_roster"] },
  { to: "/team", label: "Team schedule", short: "Team", icon: Users, perms: ["view_team"] },
  { to: "/approvals", label: "Approvals", short: "Approvals", icon: Inbox, perms: ["edit_rosters", "approve_leave"] },
  { to: "/holidays", label: "Holidays", short: "Holidays", icon: Plane },
  { to: "/projects", label: "Projects/Locations", short: "Projects", icon: FolderKanban },
  { to: "/notifications", label: "Notifications", short: "Alerts", icon: Bell },
];

export const SETTINGS_NAV = [
  { to: "/settings/users", label: "Users", icon: UserCog, perms: ["manage_users"] },
  { to: "/settings/roles", label: "Roles", icon: ShieldCheck, perms: ["manage_settings"] },
  { to: "/settings/departments", label: "Departments", icon: Building2, perms: ["manage_settings"] },
  { to: "/settings/checklists", label: "Checklists", icon: ListChecks, perms: ["manage_settings"] },
  { to: "/settings/email", label: "Email", icon: Mail, perms: ["manage_settings"] },
];

export const allowed = (user, items) => items.filter((n) => !n.perms || can(user, ...n.perms));

const slug = (to) => (to === "/" ? "calendar" : to.slice(1).replace(/\//g, "-"));

const SideLink = ({ to, label, icon: Icon }) => (
  <NavLink
    to={to}
    end={to === "/"}
    data-testid={`sidebar-nav-${slug(to)}`}
    className={({ isActive }) =>
      `group flex items-center gap-3 px-4 py-2.5 rounded-xl text-[15px] transition-colors duration-200 ${
        isActive ? "bg-white text-[#24474A] font-bold shadow-sm" : "text-white/75 hover:bg-white/10 hover:text-white"
      }`
    }
  >
    <Icon className="w-[18px] h-[18px] transition-transform duration-200 group-hover:scale-110" />
    {label}
  </NavLink>
);

const Sidebar = ({ nav, settings }) => (
  <aside className="hidden md:flex fixed inset-y-0 left-0 w-64 flex-col bg-[#24474A] text-white z-30" data-testid="desktop-sidebar">
    <div className="flex items-center gap-3 px-6 pt-7 pb-8">
      <span className="w-12 h-12 rounded-full bg-white grid place-items-center shrink-0"><img src="/logo.png" alt="JP Hospitality" className="w-11 h-11 rounded-full" data-testid="sidebar-logo" /></span>
      <div className="leading-tight">
        <div className="font-bold text-white text-lg">TSKPRO</div>
        <div className="text-[11px] tracking-[0.18em] uppercase text-white/60">Team - Task - Productivity</div>
      </div>
    </div>
    <nav className="flex-1 px-3 space-y-1 overflow-y-auto pb-6">
      {nav.map((n) => <SideLink key={n.to} {...n} />)}
      {settings.length > 0 && (
        <div className="pt-6" data-testid="sidebar-settings-group">
          <div className="flex items-center gap-2 px-4 pb-2 text-[11px] uppercase tracking-[0.18em] text-white/50"><Settings className="w-3.5 h-3.5" /> Settings</div>
          <div className="space-y-1">{settings.map((n) => <SideLink key={n.to} {...n} />)}</div>
        </div>
      )}
    </nav>
  </aside>
);

const BottomNav = ({ nav }) => (
  <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-[#24474A] border-t border-[#24474A]" data-testid="mobile-bottom-nav">
    <div className="flex overflow-x-auto no-scrollbar px-1 pb-[env(safe-area-inset-bottom)]">
      {nav.map(({ to, short, icon: Icon }) => (
        <NavLink
          key={to}
          to={to}
          end={to === "/"}
          data-testid={`bottom-nav-${slug(to)}`}
          className={({ isActive }) =>
            `flex-1 min-w-[58px] flex flex-col items-center gap-1 py-2.5 text-[11px] transition-colors ${
              isActive ? "text-[#24474A] bg-white font-bold rounded-xl my-1" : "text-white/60"
            }`
          }
        >
          <Icon className="w-5 h-5" />
          <span className="whitespace-nowrap">{short}</span>
        </NavLink>
      ))}
    </div>
  </nav>
);

const UserMenu = ({ user, logout, onPassword }) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <button data-testid="user-menu-trigger" className="flex items-center gap-2 rounded-full pl-1 pr-3 py-1 hover:bg-slate-100 transition-colors">
        <span className="w-8 h-8 rounded-full bg-[#24474A] text-white grid place-items-center text-xs font-bold">{initials(user.name)}</span>
        <span className="hidden sm:block text-left leading-tight">
          <span className="block text-sm font-bold text-slate-800">{user.name}</span>
          <span className="block text-[11px] text-slate-500">{user.role_name}{user.department ? ` · ${user.department}` : ""}</span>
        </span>
      </button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end" className="w-56">
      <DropdownMenuLabel>
        <div className="font-bold">{user.name}</div>
        <div className="text-xs text-slate-500 font-normal">{user.email}</div>
      </DropdownMenuLabel>
      <DropdownMenuSeparator />
      <DropdownMenuItem data-testid="change-password-menu-item" onClick={onPassword}>
        <KeyRound className="w-4 h-4 mr-2" /> Change password
      </DropdownMenuItem>
      <DropdownMenuItem data-testid="logout-btn" onClick={logout} className="text-rose-600">
        <LogOut className="w-4 h-4 mr-2" /> Sign out
      </DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
);

export const AppShell = ({ children }) => {
  const { user, logout } = useAuth();
  const [pwOpen, setPwOpen] = useState(false);
  const nav = allowed(user, MAIN_NAV);
  const settings = allowed(user, SETTINGS_NAV);
  const mobileNav = [
    ...nav.filter((n) => n.to !== "/notifications"),
    ...(settings.length ? [{ to: settings[0].to.replace(/\/[^/]+$/, ""), short: "Settings", icon: Settings }] : []),
  ];
  return (
    <div className="min-h-screen">
      <Sidebar nav={nav} settings={settings} />
      <div className="md:pl-64">
        <header className="sticky top-0 z-20 bg-[hsl(var(--background))]/85 backdrop-blur-xl border-b border-slate-200/70">
          <div className="flex items-center justify-between px-4 sm:px-8 h-16">
            <div className="flex items-center gap-2 md:hidden">
              <img src="/logo.png" alt="JP Hospitality" className="w-9 h-9" />
              <span className="font-bold text-[#24474A]">TSKPRO</span>
            </div>
            <div className="hidden md:block text-sm text-slate-500" data-testid="header-role-label">
              Signed in as <span className="font-bold text-[#24474A]">{user.role_name}</span>
            </div>
            <div className="flex items-center gap-2">
              <NotificationBell />
              <UserMenu user={user} logout={logout} onPassword={() => setPwOpen(true)} />
            </div>
          </div>
        </header>
        <main className="px-4 sm:px-8 py-6 sm:py-8 pb-28 md:pb-10 max-w-[1600px]">{children}</main>
      </div>
      <BottomNav nav={mobileNav} />
      <ChangePasswordDialog open={pwOpen} onClose={() => setPwOpen(false)} />
    </div>
  );
};
